import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, Folder, Moon, Search, Settings, Sun, Users, Upload, CornerDownLeft, Trash2 } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { useUiStore } from '@/stores/ui';
import { useSessionStore } from '@/stores/session';
import { Dialog } from '@/components/Dialog';
import { cx } from '@/lib/format';
import { isEditableTarget } from '@/lib/keyboard';
import { useBoard } from '@/lib/api/queries';

type Cmd = { id: string; label: string; hint?: string; icon: React.ReactNode; run: () => void };

export function CommandPalette() {
  const open = useUiStore((s) => s.paletteOpen);
  const setOpen = useUiStore((s) => s.setPaletteOpen);
  const setTheme = useUiStore((s) => s.setTheme);
  const { ws = '', boardId, assetId } = useParams(); // release3/B: assetId when the overlay is open
  const navigate = useNavigate();
  const qc = useQueryClient();
  const smartBoard = useBoard(boardId).data?.kind === 'smart'; // release2/A: no upload into a live query
  const wsId = useSessionStore((s) => s.workspace?.id);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!useUiStore.getState().paletteOpen);
      } else if (e.key === '?' && !isEditableTarget(e) && !e.metaKey && !e.ctrlKey) {
        useUiStore.getState().setShortcutsOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setOpen]);

  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  const boards = useMemo(() => {
    if (!open) return [] as Board[];
    const out: Board[] = [];
    for (const query of qc.getQueryCache().findAll({ queryKey: ['library-boards'] })) for (const b of (query.state.data as Board[] | undefined) ?? []) out.push(b);
    for (const query of qc.getQueryCache().findAll({ queryKey: ['board-children'] })) for (const b of (query.state.data as Board[] | undefined) ?? []) out.push(b);
    for (const query of qc.getQueryCache().findAll({ queryKey: ['board'] })) {
      const b = query.state.data as Board | undefined;
      if (b?.id) out.push(b);
    }
    const seen = new Set<string>();
    return out.filter((b) => (seen.has(b.id) ? false : (seen.add(b.id), true)));
  }, [open, qc]);

  const cmds = useMemo<Cmd[]>(() => {
    const go = (to: string) => () => navigate(to);
    const base: Cmd[] = [
      { id: 'search', label: q.trim() ? `Search “${q.trim()}”` : 'Search assets', icon: <Search size={14} />, run: go(`/w/${ws}/search?q=${encodeURIComponent(q.trim())}`) },
      { id: 'people', label: 'Go to People', icon: <Users size={14} />, run: go(`/w/${ws}/people`) },
      { id: 'duplicates', label: 'Find duplicates', icon: <Copy size={14} />, run: go(`/w/${ws}/duplicates`) },
      { id: 'trash', label: 'Go to Trash', icon: <Trash2 size={14} />, run: go(`/w/${ws}/trash`) },
      { id: 'settings', label: 'Workspace settings', icon: <Settings size={14} />, run: go(`/w/${ws}/settings`) },
      { id: 'light', label: 'Theme: light', icon: <Sun size={14} />, run: () => setTheme('light') },
      { id: 'dark', label: 'Theme: dark', icon: <Moon size={14} />, run: () => setTheme('dark') },
    ];
    if (boardId && !smartBoard) base.unshift({ id: 'upload', label: 'Upload files to this board', icon: <Upload size={14} />, run: () => document.querySelector<HTMLInputElement>('input[type=file]:not([webkitdirectory])')?.click() });
    if (assetId) base.unshift({ id: 'similar', label: 'Find similar to this asset', hint: 'Similar strip', icon: <Search size={14} />, run: go(`/w/${ws}/a/${assetId}?similar=1`) }); // release3/B
    const boardCmds = boards.map<Cmd>((b) => ({ id: `b:${b.id}`, label: b.name, hint: (b.breadcrumbs ?? []).slice(0, -1).map((c) => c.name).join(' / ') || b.path, icon: <Folder size={14} style={{ color: b.color ?? undefined }} />, run: go(`/w/${ws}/b/${b.id}`) }));
    const needle = q.trim().toLowerCase();
    const all = [...boardCmds, ...base];
    if (!needle) return [...boardCmds.slice(0, 8), ...base];
    return all.filter((c) => c.label.toLowerCase().includes(needle) || c.hint?.toLowerCase().includes(needle) || c.id === 'search').slice(0, 12);
  }, [q, boards, ws, boardId, assetId, smartBoard, navigate, setTheme]); // release3/B: assetId

  if (!open || !wsId) return null;
  const run = (c: Cmd) => {
    setOpen(false);
    c.run();
  };
  return (
    <Dialog open bare onClose={() => setOpen(false)} title="Command palette" width="max-w-lg">
      <input
        ref={input}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(cmds.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          }
          else if (e.key === 'Enter' && cmds[active]) run(cmds[active]);
        }}
        placeholder="Jump to a board, search, or run a command…"
        aria-label="Command"
        role="combobox"
        aria-expanded
        aria-controls="palette-list"
        aria-activedescendant={cmds[active] ? `cmd-${cmds[active].id}` : undefined}
        className="h-11 w-full border-b border-line bg-transparent px-4 text-sm outline-none"
      />
      <ul id="palette-list" role="listbox" className="max-h-80 overflow-y-auto p-1">
        {cmds.map((c, i) => (
          <li
            key={c.id}
            id={`cmd-${c.id}`}
            role="option"
            aria-selected={i === active}
            onMouseEnter={() => setActive(i)}
            onClick={() => run(c)}
            className={cx('flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm', i === active && 'bg-accent-soft')}
          >
            <span className="text-fg-muted">{c.icon}</span>
            <span className="truncate">{c.label}</span>
            {c.hint && <span className="ml-1 truncate text-xs text-fg-subtle">{c.hint}</span>}
            {i === active && <CornerDownLeft size={12} className="ml-auto text-fg-subtle" />}
          </li>
        ))}
        {cmds.length === 0 && <li className="px-3 py-4 text-center text-sm text-fg-subtle">No matches</li>}
      </ul>
    </Dialog>
  );
}
